import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, Search, Target, Rocket, RefreshCw, ArrowRight, ArrowUpRight } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 }
};

const steps = [
  {
    num: "01",
    icon: Phone,
    title: "Appel découverte",
    desc: "Un échange rapide pour comprendre votre activité, vos enjeux et vos objectifs. C'est le point de départ de notre collaboration.",
    details: [
      "Compréhension de votre métier et contexte",
      "Identification de vos priorités",
      "Évaluation de vos besoins immédiats"
    ]
  },
  {
    num: "02",
    icon: Search,
    title: "Diagnostic de l'activité",
    desc: "Analyse approfondie de votre organisation, de vos outils et de vos processus pour identifier les points d'amélioration à fort impact.",
    details: [
      "Audit de votre organisation actuelle",
      "Analyse de vos outils numériques",
      "Cartographie des opportunités"
    ]
  },
  {
    num: "03",
    icon: Target,
    title: "Stratégie digitale",
    desc: "Définition d'un plan d'action concret et priorisé, avec les améliorations les plus impactantes pour votre activité.",
    details: [
      "Plan d'action personnalisé",
      "Priorisation des chantiers",
      "Budget et timeline clairs"
    ]
  },
  {
    num: "04",
    icon: Rocket,
    title: "Mise en œuvre",
    desc: "Déploiement des sites internet, outils de gestion, automatisations ou solutions IA selon les priorités définies ensemble.",
    details: [
      "Déploiement progressif des solutions",
      "Formation et accompagnement",
      "Tests et validation"
    ]
  },
  {
    num: "05",
    icon: RefreshCw,
    title: "Optimisation continue",
    desc: "Suivi régulier et amélioration continue de vos systèmes pour maximiser votre retour sur investissement.",
    details: [
      "Suivi des performances",
      "Ajustements et améliorations",
      "Support et assistance"
    ]
  }
];

export default function ProcessPage() {
  return (
    <div data-testid="process-page">
      {/* Header */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#59C3A6]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div {...fadeUp}>
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Notre méthode</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 mb-6 text-white">
              Un processus{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#59C3A6] to-[#4AB095]">
                clair et structuré
              </span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              De l'analyse initiale à l'optimisation continue, chaque étape est conçue pour maximiser l'impact sur votre activité.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-4xl mx-auto relative">
          {/* Timeline line */}
          <div className="absolute left-8 md:left-12 top-0 bottom-0 w-px bg-gradient-to-b from-[#59C3A6]/30 via-[#59C3A6]/10 to-transparent hidden md:block" />

          <div className="space-y-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`process-step-${i}`}
                className="relative"
              >
                <div className="flex gap-6 md:gap-10">
                  {/* Step number */}
                  <div className="relative z-10 shrink-0">
                    <div className="w-16 h-16 md:w-24 md:h-24 rounded-2xl bg-[#112A4F]/50 border border-[#59C3A6]/20 flex items-center justify-center">
                      <span className="font-heading text-2xl md:text-3xl font-bold text-[#59C3A6]">{step.num}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-8">
                    <div className="p-6 md:p-8 rounded-2xl bg-[#112A4F]/30 border border-white/5 hover:border-[#59C3A6]/15 transition-all duration-300">
                      <div className="flex items-center gap-3 mb-3">
                        <step.icon size={20} className="text-[#59C3A6]" />
                        <h3 className="font-heading text-xl md:text-2xl font-semibold text-white">{step.title}</h3>
                      </div>
                      <p className="text-slate-300 text-base leading-relaxed mb-5">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.details.map((detail, j) => (
                          <li key={j} className="flex items-center gap-2 text-slate-400 text-sm">
                            <span className="w-1 h-1 rounded-full bg-[#59C3A6]" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#071428]">
        <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-5">
            Prêt à commencer ?
          </h2>
          <p className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed">
            La première étape est un simple appel pour discuter de votre activité. C'est gratuit et sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="process-cta"
                className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(89,195,166,0.25)] text-base"
              >
                Demander un diagnostic <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <a href="https://calendly.com/finoveo-conseil/30min" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="process-calendly"
                variant="outline"
                className="border-[#59C3A6]/30 text-[#59C3A6] hover:bg-[#59C3A6]/10 hover:text-[#59C3A6] px-8 py-3 h-auto rounded-full transition-all duration-300 text-base bg-transparent"
              >
                Réserver un appel <ArrowUpRight className="ml-1" size={16} />
              </Button>
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
